import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import { GoogleMap, LoadScript, Marker, Polygon, InfoWindow } from '@react-google-maps/api';
import './MapComponent.css';
import { GOOGLE_MAPS_API_KEY, calculatePolygonArea, handleFileDrop } from '../services/mapService';
import { FaDrawPolygon, FaSearchLocation, FaExpand, FaCompress, FaUpload, FaMapMarker, FaCheck, FaTimes, FaUndo, FaRuler, FaTrash } from 'react-icons/fa';

// Field fill colour based on crop type — matches Fields.js cropTypes palette
const getFieldColor = (cropType) => {
  const colors = {
    wheat:     '#E8A838', // amber gold
    corn:      '#3B82F6', // blue
    sunflower: '#FACC15', // bright yellow
    rapeseed:  '#84CC16', // yellow-green
    alfalfa:   '#A855F7', // purple
    soybean:   '#F97316', // orange
    fallow:    '#94A3B8', // slate gray
  };
  return colors[cropType] || '#2ecc71'; // default green
};

// Libraries to load with Google Maps
const libraries = ["geometry"];

// Stable center reference — must be module-level so it never changes between renders.
// A new object on each render would cause @react-google-maps/api to call map.panTo() on every re-render.
const MAP_DEFAULT_CENTER = {
  lat: 47.056414,
  lng: 26.846064
};

// Stable map options — module-level so Google Maps never sees a changed reference.
// If this were created inside the component, every React re-render would cause Google Maps
// to reconfigure itself (reset controls, redraw tiles) which is the main source of pan/zoom lag.
const MAP_OPTIONS = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: true,
  scaleControl: true,
  streetViewControl: true,
  rotateControl: true,
  fullscreenControl: false,
  mapTypeId: 'satellite',
  gestureHandling: 'greedy',
};

const MapComponent = forwardRef(({ fields, onFieldSelect, onDrawField, importedPolygons, onMapLoad, onImportKML, editingFieldId, onShapeEdited, splitMode, splitSourceFieldId, onEditField, onEditShape, onSplitField, onDeleteField, selectedYear, hasPendingShapeChanges, onSaveShape, onCancelShape }, ref) => {
  const [map, setMap] = useState(null);
  const [drawingMode, setDrawingMode] = useState(false);
  const [polygonPath, setPolygonPath] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [areaPreview, setAreaPreview] = useState(null);
  const [infoPosition, setInfoPosition] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [locationPermission, setLocationPermission] = useState('prompt');
  const [showLocationRequest, setShowLocationRequest] = useState(false);
  const [showBoundaries,    setShowBoundaries]    = useState(false);
  const [communeBoundaries, setCommuneBoundaries] = useState([]); // [{name, rings:[{lat,lng}[]]}]
  const [loadingBoundaries, setLoadingBoundaries] = useState(false);
  const boundaryCache = useRef({});
  
  const mapContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Memoize container style — only changes when fullscreen toggles (avoids Google Maps resize on every render)
  const mapContainerStyle = useMemo(() => ({
    width: '100%',
    height: mapFullscreen ? '100vh' : '500px',
  }), [mapFullscreen]);

  // Check if device is mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Request location permission and get user location
  const requestLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser.');
      return;
    }

    setLocationPermission('requesting');
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setUserLocation(location);
        setLocationPermission('granted');
        setShowLocationRequest(false);
        
        // Center map on user location if in drawing mode
        if (map && drawingMode) {
          map.setCenter(location);
          map.setZoom(18); // High zoom for accurate field drawing
        }
      },
      (error) => {
        console.error('Error getting location:', error);
        setLocationPermission('denied');
        setShowLocationRequest(false);
        
        let errorMessage = 'Could not get your location. ';
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Location access was denied.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage += 'Location request timed out.';
            break;
          default:
            errorMessage += 'An unknown error occurred.';
        }
        alert(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  };

  // Show location request on mobile when drawing mode is enabled
  useEffect(() => {
    if (isMobile && drawingMode && locationPermission === 'prompt') {
      setShowLocationRequest(true);
    }
  }, [drawingMode, isMobile, locationPermission]);
  
  // Function to show a field on the map
  const handleShowFieldOnMap = (field) => {
    if (map && field) {
      showFieldOnMap(map, field);
    }
  };
  
  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    setDrawingMode: (enabled) => {
      handleDrawingMode(enabled);
    },
    showFieldOnMap: (field) => {
      if (field && map) {
        // Clear any existing info windows first
        setSelectedField(null);
        setInfoPosition(null);
        
        // Small delay to ensure the UI updates
        setTimeout(() => {
          setSelectedField(field);
          
          // Calculate center if not available
          const center = field.center || {
            lat: field.coordinates.reduce((sum, point) => sum + point.lat, 0) / field.coordinates.length,
            lng: field.coordinates.reduce((sum, point) => sum + point.lng, 0) / field.coordinates.length
          };
          
          setInfoPosition(center);
          showFieldOnMap(map, field);
        }, 50);
      }
    }
  }));
  
  // Update when fields or imported polygons change
  useEffect(() => {
    // If the selected field no longer exists (was deleted), deselect it
    if (selectedField) {
      const stillExists = 
        (fields && fields.some(f => f.id === selectedField.id)) ||
        (importedPolygons && importedPolygons.some(p => p.id === selectedField.id));
        
      if (!stillExists) {
        setSelectedField(null);
        setInfoPosition(null);
      }
    }
  }, [fields, importedPolygons, selectedField]);
  
  useEffect(() => {
    // Load city names and features when map is ready
    if (map) {
      // Enable map labels for cities, roads, etc.
      map.setOptions({
        styles: [
          {
            featureType: "all",
            elementType: "labels",
            stylers: [{ visibility: "on" }]
          }
        ]
      });
      
      if (onMapLoad) {
        onMapLoad(map);
      }
    }
  }, [map, onMapLoad]);
  
  // ── Commune boundary fetching via Overpass API (OpenStreetMap) ──────────────
  //
  // Romanian communes = admin_level=8 in OSM.  We POST a bbox-constrained
  // Overpass query so we only pick up the right commune (not a same-named one
  // in another county) and properly assemble the ring from ordered OSM ways.

  // Chain OSM way-geometry arrays into one closed ring.
  const assembleRing = (ways) => {
    if (!ways.length) return [];
    let remaining = ways.map(w => w.geometry ? [...w.geometry] : []).filter(g => g.length);
    const ring = [...remaining[0]];
    remaining = remaining.slice(1);

    while (remaining.length > 0) {
      const tail = ring[ring.length - 1];
      let matched = false;
      for (let i = 0; i < remaining.length; i++) {
        const seg = remaining[i];
        const dFwd = Math.hypot(tail.lat - seg[0].lat,              tail.lon - seg[0].lon);
        const dRev = Math.hypot(tail.lat - seg[seg.length-1].lat,   tail.lon - seg[seg.length-1].lon);
        if (dFwd < 0.00005)      { ring.push(...seg.slice(1));              remaining.splice(i,1); matched=true; break; }
        else if (dRev < 0.00005) { ring.push(...[...seg].reverse().slice(1)); remaining.splice(i,1); matched=true; break; }
      }
      if (!matched) { ring.push(...remaining[0]); remaining.splice(0,1); } // gap — just append
    }
    return ring.map(p => ({ lat: p.lat, lng: p.lon }));
  };

  // Query Overpass for a single commune boundary. Returns [{lat,lng}[]] or null.
  //
  // PRIMARY strategy: "is_in" point query — asks Overpass which admin_level=8
  // polygon *geometrically contains* the field centroid.  This is always correct
  // regardless of how many communes share similar names in the wider area.
  //
  // FALLBACK: if is_in returns nothing (happens for some rural areas where OSM
  // boundary geometry is incomplete), we fall back to a tight bbox + name search
  // with a smaller 0.15° buffer (~16 km) so we can't accidentally pick a
  // same-named commune in a neighbouring county.
  const fetchBoundaryForComune = async (comuneName, centerLat, centerLng) => {
    if (boundaryCache.current[comuneName]) return boundaryCache.current[comuneName];

    const buildRings = (el) => {
      const outerWays = el.members?.filter(m => m.role === 'outer' && m.geometry?.length) || [];
      if (!outerWays.length) return null;
      const ring = assembleRing(outerWays);
      return ring.length ? [ring] : null;
    };

    try {
      // ── Primary: is_in query ────────────────────────────────────────────────
      // "Which administrative commune polygon contains this exact lat/lng point?"
      const isInQuery =
        `[out:json];` +
        `is_in(${centerLat},${centerLng});` +
        `rel(pivot)["admin_level"="8"]["boundary"="administrative"];` +
        `out geom;`;

      const isInRes = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: isInQuery
      });

      if (isInRes.ok) {
        const isInData = await isInRes.json();
        const isInEls = isInData.elements?.filter(e => e.type === 'relation') || [];
        if (isInEls.length > 0) {
          const rings = buildRings(isInEls[0]);
          if (rings) {
            boundaryCache.current[comuneName] = rings;
            return rings;
          }
        }
      }

      // ── Fallback: tight bbox + name search ──────────────────────────────────
      const buf  = 0.15; // ~16 km — tight enough to stay within the right county
      const bbox = `${centerLat-buf},${centerLng-buf},${centerLat+buf},${centerLng+buf}`;

      const runNameQuery = async (name) => {
        const q =
          `[out:json][bbox:${bbox}];` +
          `relation["name"="${name}"]["admin_level"="8"]["boundary"="administrative"];` +
          `out geom;`;
        const r = await fetch('https://overpass-api.de/api/interpreter', {
          method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: q
        });
        if (!r.ok) return [];
        const d = await r.json();
        return d.elements?.filter(e => e.type === 'relation') || [];
      };

      let elements = await runNameQuery(comuneName);

      // Also try without diacritics (some OSM entries use transliterated names)
      if (!elements.length) {
        const plain = comuneName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        if (plain !== comuneName) elements = await runNameQuery(plain);
      }

      if (!elements.length) return null;

      // Pick the relation whose outer-way centroid is nearest to the field centroid
      let best = null, bestDist = Infinity;
      for (const el of elements) {
        const outer = el.members?.filter(m => m.role === 'outer' && m.geometry?.length) || [];
        if (!outer.length) continue;
        const pts = outer.flatMap(w => w.geometry);
        const eLat = pts.reduce((s,p) => s+p.lat, 0) / pts.length;
        const eLon = pts.reduce((s,p) => s+p.lon, 0) / pts.length;
        const dist = Math.hypot(eLat - centerLat, eLon - centerLng);
        if (dist < bestDist) { bestDist = dist; best = el; }
      }
      if (!best) return null;

      const rings = buildRings(best);
      if (rings) boundaryCache.current[comuneName] = rings;
      return rings;

    } catch (e) {
      console.error('Overpass boundary fetch failed for', comuneName, e);
      return null;
    }
  };

  // Fetch boundaries for all unique communes found in the fields prop.
  const fetchCommuneBoundaries = async () => {
    if (!fields?.length) return;
    setLoadingBoundaries(true);

    // comuneMap: name → { lats, lngs } to compute centroid
    const comuneMap = {};
    fields.forEach(f => {
      if (!f.comuna || !f.coordinates?.length) return;
      if (!comuneMap[f.comuna]) comuneMap[f.comuna] = { lats: [], lngs: [] };
      f.coordinates.forEach(c => { comuneMap[f.comuna].lats.push(c.lat); comuneMap[f.comuna].lngs.push(c.lng); });
    });

    const results = [];
    for (const [name, { lats, lngs }] of Object.entries(comuneMap)) {
      const cLat = lats.reduce((a,b) => a+b) / lats.length;
      const cLng = lngs.reduce((a,b) => a+b) / lngs.length;
      const rings = await fetchBoundaryForComune(name, cLat, cLng);
      if (rings) results.push({ name, rings });
      await new Promise(r => setTimeout(r, 500)); // be polite to Overpass API
    }

    setCommuneBoundaries(results);
    setLoadingBoundaries(false);
  };

  // Trigger boundary fetch when toggled on; clear when toggled off
  useEffect(() => {
    if (showBoundaries) {
      fetchCommuneBoundaries();
    } else {
      setCommuneBoundaries([]);
    }
  }, [showBoundaries]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate area during drawing
  useEffect(() => {
    if (polygonPath.length >= 3) {
      const area = calculatePolygonArea(polygonPath);
      setAreaPreview(area.toFixed(2));
    } else {
      setAreaPreview(null);
    }
  }, [polygonPath]);
  
  // Handle drag and drop for KML files
  useEffect(() => {
    const mapElement = mapContainerRef.current;
    
    if (!mapElement || !onImportKML) return;
    
    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      mapElement.classList.add('drag-over');
    };
    
    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      mapElement.classList.remove('drag-over');
    };
    
    const onDrop = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      mapElement.classList.remove('drag-over');
      
      try {
        const parsedPolygons = await handleFileDrop(e);
        if (parsedPolygons.length > 0) {
          onImportKML(parsedPolygons);
        }
      } catch (error) {
        console.error('Error handling file drop:', error);
        alert(error.message || 'Error importing KML file');
      }
    };
    
    // Add event listeners
    mapElement.addEventListener('dragover', handleDragOver);
    mapElement.addEventListener('dragleave', handleDragLeave);
    mapElement.addEventListener('drop', onDrop);
    
    // Clean up
    return () => {
      mapElement.removeEventListener('dragover', handleDragOver);
      mapElement.removeEventListener('dragleave', handleDragLeave);
      mapElement.removeEventListener('drop', onDrop);
    };
  }, [mapContainerRef, onImportKML]);

  // Handle map click for drawing
  const handleMapClick = (event) => {
    if (!event || !event.latLng) return;

    if (drawingMode) {
      const newPoint = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };
      setPolygonPath(prevPath => [...prevPath, newPoint]);
    } else {
      // If clicking outside polygons, deselect
      setSelectedField(null);
      setInfoPosition(null);
    }
  };
  
  // Handle drawing mode toggle
  const handleDrawingMode = (enabled) => {
    setDrawingMode(enabled);
    if (!enabled) {
      setPolygonPath([]);
      setAreaPreview(null);
    }
  };
  
  // Handle finish drawing
  const handleFinishDrawing = () => {
    if (polygonPath.length >= 3) {
      // Create a complete polygon coordinates object with proper structure
      // Calculate center of the polygon
      const centerLat = polygonPath.reduce((sum, point) => sum + point.lat, 0) / polygonPath.length;
      const centerLng = polygonPath.reduce((sum, point) => sum + point.lng, 0) / polygonPath.length;
      
      // Create a field object with all required properties
      const fieldData = {
        coordinates: [...polygonPath], // Ensure it's an array of {lat, lng} objects
        center: { lat: centerLat, lng: centerLng },
        area: areaPreview
      };
      
      // Call the onFieldDrawn callback with the field data
      if (onDrawField) {
        onDrawField(fieldData.coordinates);
      }
      
      // Exit drawing mode
      handleDrawingMode(false);
    } else {
      alert("Please place at least 3 points to complete a field boundary");
    }
  };
  
  // Handle clear drawing
  const handleClearDrawing = () => {
    setPolygonPath([]);
    setAreaPreview(null);
  };
  
  // Handle remove last point
  const handleRemoveLastPoint = () => {
    if (polygonPath.length > 0) {
      setPolygonPath(prevPath => prevPath.slice(0, -1));
    }
  };
  
  // Add a simple visual helper for field click
  const handleFieldClick = (field, centerPoint, e) => {
    // Check if e is a valid event with stopPropagation
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation(); // Prevent deselecting through map click
    }
    
    // Prevent the default behavior which can cause map movement
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    
    // First clear any existing selection to ensure clean state
    setSelectedField(null);
    setInfoPosition(null);
    
    // Show a brief visual confirmation to help seniors
    if (map) {
      // Temporarily freeze the map for better experience
      map.setOptions({ draggable: false });
      
      // Show visual feedback - briefly highlight the field
      setTimeout(() => {
        setSelectedField(field);
        if (centerPoint) {
          setInfoPosition({
            lat: centerPoint.lat,
            lng: centerPoint.lng
          });
        }
        
        if (onFieldSelect) {
          onFieldSelect(field);
        }
        
        // Re-enable map dragging after a brief delay
        setTimeout(() => {
          map.setOptions({ draggable: true });
        }, 600);
      }, 100);
    }
  };
  
  const toggleFullscreen = () => {
    setMapFullscreen(!mapFullscreen);
    
    // Give time for the container to resize, then trigger map resize
    setTimeout(() => {
      if (map && window.google) {
        const center = map.getCenter();
        window.google.maps.event.trigger(map, 'resize');
        map.setCenter(center);
      }
    }, 100);
  };
  
  const getFieldArea = (field) => {
    if (field.area) return field.area;
    
    if (field.coordinates && field.coordinates.length >= 3) {
      return calculatePolygonArea(field.coordinates).toFixed(2);
    } else if (field.path && field.path.length >= 3) {
      return calculatePolygonArea(field.path).toFixed(2);
    }
    
    return "N/A";
  };
  
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && onImportKML) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const fileContent = e.target.result;
          const fileExtension = file.name.split('.').pop().toLowerCase();
          
          if (fileExtension === 'kml') {
            // Process KML file
            const parser = new DOMParser();
            const kmlDoc = parser.parseFromString(fileContent, 'text/xml');
            
            // Parse polygons from KML
            const polygons = [];
            const placemarks = kmlDoc.getElementsByTagName('Placemark');
            
            for (let i = 0; i < placemarks.length; i++) {
              const placemark = placemarks[i];
              const name = placemark.getElementsByTagName('name')[0]?.textContent || `Field ${i + 1}`;
              const polygonElements = placemark.getElementsByTagName('Polygon');
              
              if (polygonElements.length > 0) {
                const coordinates = placemark.getElementsByTagName('coordinates')[0]?.textContent;
                
                if (coordinates) {
                  const points = coordinates.trim()
                    .split(/\s+/)
                    .filter(coord => coord.trim() !== '')
                    .map(coord => {
                      const [lng, lat, _] = coord.split(',').map(parseFloat);
                      return { lat, lng };
                    });
                  
                  if (points.length >= 3) {
                    const area = calculatePolygonArea(points);
                    polygons.push({
                      id: `imported-${Date.now()}-${i}`,
                      name,
                      coordinates: points,
                      area: area.toFixed(2),
                      center: {
                        lat: points.reduce((sum, point) => sum + point.lat, 0) / points.length,
                        lng: points.reduce((sum, point) => sum + point.lng, 0) / points.length
                      },
                      imported: true
                    });
                  }
                }
              }
            }
            
            if (polygons.length > 0) {
              onImportKML(polygons);
            } else {
              alert('No valid polygon data found in the KML file.');
            }
          } else {
            alert('Please upload a valid KML file.');
          }
        } catch (error) {
          console.error('Error parsing file:', error);
          alert('Failed to parse the file. Please make sure it is a valid KML file.');
        }
      };
      reader.readAsText(file);
      
      // Reset the input value
      event.target.value = '';
    }
  };
  
  // Handle map loading and errors
  const handleOnLoad = (mapInstance) => {
    setMap(mapInstance);
    setIsLoaded(true);
  };
  
  const handleLoadError = (error) => {
    console.error('Error loading Google Maps:', error);
    setLoadError(error);
  };
  
  // If there's a loading error
  if (loadError) {
    return (
      <div className="map-error">
        <p>Error loading Google Maps: {loadError.message}</p>
        <button onClick={() => window.location.reload()}>Reload</button>
      </div>
    );
  }
  
  // Determine drawing status text
  const getDrawingStatusText = () => {
    if (splitMode) {
      if (polygonPath.length === 0) return 'Click pe hartă pentru a trasa secțiunea de tăiat';
      if (polygonPath.length < 3)   return `${polygonPath.length} puncte — mai adaugă cel puțin ${3 - polygonPath.length}`;
      return `${polygonPath.length} puncte. Click „Aplică Tăierea" când ați terminat`;
    }
    if (polygonPath.length === 0) {
      return "Click pe hartă pentru a plasa primul punct";
    } else if (polygonPath.length === 1) {
      return "Click pentru al doilea punct";
    } else if (polygonPath.length === 2) {
      return "Click pentru al treilea punct (minim necesar)";
    } else {
      return `${polygonPath.length} puncte plasate. Click „Salvează Câmpul" când ați terminat`;
    }
  };
  
  return (
    <div 
      ref={mapContainerRef}
      className={`map-container ${mapFullscreen ? 'fullscreen' : ''}`}
    >
      <LoadScript 
        googleMapsApiKey={GOOGLE_MAPS_API_KEY} 
        libraries={libraries}
        onLoad={() => console.log('Google Maps Script loaded.')}
        onError={handleLoadError}
        loadingElement={<div className="map-loading"><p>Loading Google Maps...</p></div>}
      >
        {!isLoaded && (
          <div className="map-loading">
            <p>Loading map...</p>
          </div>
        )}
        
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={MAP_DEFAULT_CENTER}
          zoom={13}
          options={MAP_OPTIONS}
          onClick={handleMapClick}
          onLoad={handleOnLoad}
        >
          {/* Draw existing fields */}
          {isLoaded && fields && fields.map((field) => {
            if (!field.coordinates || field.coordinates.length < 3) return null;
            
            const center = field.center || {
              lat: field.coordinates.reduce((sum, point) => sum + point.lat, 0) / field.coordinates.length,
              lng: field.coordinates.reduce((sum, point) => sum + point.lng, 0) / field.coordinates.length
            };
            
            return (
              <React.Fragment key={field.id}>
                <Polygon
                  paths={field.coordinates}
                  onClick={(e) => {
                    if (e) {
                      if (e.domEvent?.stopPropagation) e.domEvent.stopPropagation();
                      if (typeof e.stop === 'function') e.stop();
                    }
                    if (map) {
                      map.setOptions({ draggable: false });
                      setTimeout(() => map.setOptions({ draggable: true }), 100);
                    }
                    handleFieldClick(field, center, { stopPropagation: () => {}, preventDefault: () => {} });
                  }}
                  onMouseUp={(e) => {
                    // Capture updated coordinates after vertex drag in edit mode
                    if (editingFieldId === field.id && e?.domEvent?.target) {
                      // Read path from the polygon instance via the map
                      try {
                        if (e.latLng) {
                          // The polygon's path has been updated by Google Maps; read it back
                          // We need a ref to the polygon — use a timeout to let Maps finish updating
                          setTimeout(() => {
                            if (map && onShapeEdited) {
                              // Find polygon overlay by iterating map overlays
                              // Instead, we'll rely on onLoad ref
                            }
                          }, 50);
                        }
                      } catch (_) {}
                    }
                  }}
                  onLoad={(poly) => {
                    // Store ref to editable polygon so we can read updated path
                    if (editingFieldId === field.id) {
                      poly.addListener('mouseup', () => {
                        const path = poly.getPath();
                        const coords = path.getArray().map(p => ({ lat: p.lat(), lng: p.lng() }));
                        if (onShapeEdited) onShapeEdited(coords);
                      });
                    }
                  }}
                  options={(() => {
                    // Priority: split > editing > selected > special state > crop/default
                    const isSplit    = field.id === splitSourceFieldId;
                    const isEditing  = editingFieldId === field.id;
                    const isSelected = field.id === selectedField?.id;
                    const isPasune   = field.folosinta === 'pasune';
                    const isApia     = field.apiaOnly;
                    const hasCrop    = !!field.cropType;

                    let fill, stroke, fillOp, strokeOp;

                    if (isSplit)       { fill = '#FF3333'; stroke = '#CC0000'; fillOp = 0.22; strokeOp = 1; }
                    else if (isEditing){ fill = '#FF9800'; stroke = '#FF6600'; fillOp = 0.35; strokeOp = 1; }
                    else if (isSelected){ fill = '#3498db'; stroke = '#2980b9'; fillOp = 0.6; strokeOp = 1; }
                    else if (isApia)   { fill = '#E53935'; stroke = '#B71C1C'; fillOp = 0.25; strokeOp = 0.8; }
                    else if (isPasune) { fill = '#2D6A2D'; stroke = '#1B4D1B'; fillOp = 0.45; strokeOp = 1; }
                    else if (!hasCrop) { fill = '#FFFFFF'; stroke = '#9CA3AF'; fillOp = 0.4;  strokeOp = 0.9; }
                    else               { fill = getFieldColor(field.cropType); stroke = '#27ae60'; fillOp = 0.45; strokeOp = 1; }

                    return {
                      fillColor:    fill,
                      fillOpacity:  fillOp,
                      strokeColor:  stroke,
                      strokeWeight: (isSplit || isEditing) ? 3 : isSelected ? 2.5 : 1.5,
                      strokeOpacity: strokeOp,
                      editable:  isEditing,
                      draggable: false,
                      clickable: !drawingMode
                    };
                  })()}
                />
                <Marker
                  position={center}
                  clickable={!drawingMode}
                  label={{
                    text: field.name || 'Unnamed Field',
                    color: 'white',
                    className: field.id === selectedField?.id ? 'field-label selected' : 'field-label'
                  }}
                  icon={{
                    path: window.google && window.google.maps ? window.google.maps.SymbolPath.CIRCLE : {},
                    scale: 0
                  }}
                  onClick={(e) => {
                    if (e) {
                      if (e.domEvent && typeof e.domEvent.stopPropagation === 'function') {
                        e.domEvent.stopPropagation();
                      }
                      if (typeof e.stop === 'function') e.stop();
                    }
                    if (map) {
                      map.setOptions({ draggable: false });
                      setTimeout(() => map.setOptions({ draggable: true }), 100);
                    }
                    handleFieldClick(field, center, {
                      stopPropagation: () => {},
                      preventDefault: () => {}
                    });
                  }}
                />
              </React.Fragment>
            );
          })}
          
          {/* Draw imported polygons */}
          {isLoaded && importedPolygons && importedPolygons.map((polygon) => {
            if (!polygon.coordinates || polygon.coordinates.length < 3) return null;
            
            const center = polygon.center || {
              lat: polygon.coordinates.reduce((sum, point) => sum + point.lat, 0) / polygon.coordinates.length,
              lng: polygon.coordinates.reduce((sum, point) => sum + point.lng, 0) / polygon.coordinates.length
            };
            
            return (
              <React.Fragment key={polygon.id}>
                <Polygon
                  paths={polygon.coordinates}
                  onClick={(e) => {
                    if (e) {
                      // Prevent the default Google Maps behavior
                      if (e.domEvent && typeof e.domEvent.stopPropagation === 'function') {
                        e.domEvent.stopPropagation();
                      }
                      // Stop any other propagation
                      if (typeof e.stop === 'function') {
                        e.stop();
                      }
                    }
                    
                    // Don't move the map when selecting a field
                    if (map) {
                      map.setOptions({ draggable: false });
                      setTimeout(() => map.setOptions({ draggable: true }), 100);
                    }
                    
                    handleFieldClick(polygon, center, {
                      stopPropagation: () => {},
                      preventDefault: () => {}
                    });
                  }}
                  options={{
                    fillColor: polygon.id === selectedField?.id ? '#3498db' : '#f39c12',
                    fillOpacity: polygon.id === selectedField?.id ? 0.6 : 0.4,
                    strokeColor: polygon.id === selectedField?.id ? '#2980b9' : '#e67e22',
                    strokeWeight: polygon.id === selectedField?.id ? 2.5 : 1.5,
                    strokeOpacity: 1,
                    strokeDashArray: polygon.id === selectedField?.id ? null : [5, 5]
                  }}
                />
                <Marker
                  position={center}
                  clickable={!drawingMode}
                  label={{
                    text: polygon.name || 'Imported',
                    color: 'white',
                    className: 'field-label imported'
                  }}
                  icon={{
                    path: "M 0,0",
                    scale: 0
                  }}
                  onClick={(e) => {
                    if (e) {
                      // Prevent the default Google Maps behavior
                      if (e.domEvent && typeof e.domEvent.stopPropagation === 'function') {
                        e.domEvent.stopPropagation();
                      }
                      // Stop any other propagation
                      if (typeof e.stop === 'function') {
                        e.stop();
                      }
                    }
                    
                    // Don't move the map when selecting a field
                    if (map) {
                      map.setOptions({ draggable: false });
                      setTimeout(() => map.setOptions({ draggable: true }), 100);
                    }
                    
                    handleFieldClick(polygon, center, {
                      stopPropagation: () => {},
                      preventDefault: () => {}
                    });
                  }}
                />
              </React.Fragment>
            );
          })}
          
          {/* Draw polygon in progress — clickable:false so map clicks still register */}
          {isLoaded && drawingMode && polygonPath.length >= 3 && (
            <Polygon
              paths={polygonPath}
              options={{
                fillColor: '#3498db',
                fillOpacity: 0.25,
                strokeColor: '#2980b9',
                strokeWeight: 2,
                clickable: false,   // IMPORTANT: let map clicks pass through
                zIndex: 1
              }}
            />
          )}
          
          {/* Draw polygon points — clickable:false so they don't intercept map clicks */}
          {isLoaded && drawingMode && polygonPath.map((point, index) => (
            <Marker
              key={`point-${index}`}
              position={point}
              clickable={false}
              label={{
                text: `${index + 1}`,
                color: 'white',
                className: 'point-label'
              }}
              icon={{
                path: window.google && window.google.maps ? window.google.maps.SymbolPath.CIRCLE : {},
                scale: 6,
                fillColor: index === 0 ? '#2ecc71' : '#3498db',
                fillOpacity: 1,
                strokeColor: '#FFFFFF',
                strokeWeight: 1.5
              }}
            />
          ))}
          
          {/* User location marker */}
          {isLoaded && userLocation && (
            <Marker
              position={userLocation}
              title="Your Location"
              icon={{
                path: window.google && window.google.maps ? window.google.maps.SymbolPath.CIRCLE : {},
                scale: 8,
                fillColor: '#4285F4',
                fillOpacity: 1,
                strokeColor: '#FFFFFF',
                strokeWeight: 2
              }}
            />
          )}

          {/* Commune boundary polygons fetched from Nominatim/OSM */}
          {isLoaded && showBoundaries && communeBoundaries.flatMap(({ name, rings }) =>
            rings.map((coords, i) => (
              <Polygon
                key={`cb-${name}-${i}`}
                paths={coords}
                options={{
                  fillOpacity: 0,
                  strokeColor: '#FF3B30',
                  strokeWeight: 2.5,
                  strokeOpacity: 0.9,
                  clickable: false,
                  zIndex: 50
                }}
              />
            ))
          )}

          {/* Rich info card for selected field — hidden while drawing/splitting */}
          {isLoaded && selectedField && infoPosition && !drawingMode && (
            <InfoWindow
              position={infoPosition}
              onCloseClick={() => { setSelectedField(null); setInfoPosition(null); }}
              options={{ maxWidth: 290 }}
            >
              <div className="field-info-window">
                {/* ── Header ── */}
                <div style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: 6, marginBottom: 6 }}>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a1a', marginBottom: 2 }}>
                    {selectedField.name || 'Fără Nume'}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: '#555', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    <span>📐 {getFieldArea(selectedField)} ha</span>
                    {selectedField.tNumber && <span>📋 T{selectedField.tNumber}</span>}
                    {selectedField.comuna  && <span>📍 {selectedField.comuna}</span>}
                    {selectedField.company && <span>🏢 {selectedField.company}</span>}
                  </div>
                </div>

                {/* ── Year-specific data ── */}
                {(() => {
                  const yd = selectedField.yearData?.[selectedYear] || {};
                  const crop     = yd.crop     ?? selectedField.crop     ?? '';
                  const stage    = yd.stage    ?? selectedField.stage    ?? '';
                  const health   = yd.health   ?? selectedField.health   ?? '';
                  const contract = yd.contract ?? selectedField.contract ?? '';
                  if (!crop && !stage && !health && !contract) return null;
                  return (
                    <div style={{ fontSize: '0.8rem', color: '#444', marginBottom: 6, lineHeight: 1.6 }}>
                      {selectedYear && <div style={{ fontWeight: 600, color: '#4CAF50', fontSize: '0.75rem', marginBottom: 2 }}>📅 {selectedYear}</div>}
                      {crop     && <div>🌾 {crop}</div>}
                      {stage    && <div>🌱 {stage}</div>}
                      {health   && <div>💚 {health}</div>}
                      {contract && <div>📄 {contract}</div>}
                    </div>
                  );
                })()}

                {/* ── Action buttons ── */}
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', borderTop: '1px solid #e0e0e0', paddingTop: 6 }}>
                  {selectedField.imported ? (
                    <button
                      style={{ padding: '5px 10px', background: '#4CAF50', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}
                      onClick={() => {
                        if (onFieldSelect) {
                          const nf = { ...selectedField }; delete nf.imported;
                          onFieldSelect(nf, true);
                          if (onImportKML) onImportKML(importedPolygons.filter(p => p.id !== selectedField.id));
                        }
                        setSelectedField(null); setInfoPosition(null);
                      }}
                    >➕ Convertește</button>
                  ) : (
                    <>
                      <button
                        style={{ padding: '5px 9px', background: '#2196F3', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.78rem' }}
                        onClick={(e) => { e.stopPropagation(); setSelectedField(null); setInfoPosition(null); if (onEditField) onEditField(selectedField); }}
                        title="Editează informații câmp"
                      >✏️ Editează</button>

                      {selectedField.coordinates?.length >= 3 && (
                        <button
                          style={{ padding: '5px 9px', background: '#FF9800', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.78rem' }}
                          onClick={(e) => { e.stopPropagation(); setSelectedField(null); setInfoPosition(null); if (onEditShape) onEditShape(selectedField); }}
                          title="Editează forma poligonului"
                        >🔷 Formă</button>
                      )}

                      {selectedField.coordinates?.length >= 3 && (
                        <button
                          style={{ padding: '5px 9px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.78rem' }}
                          onClick={(e) => { e.stopPropagation(); setSelectedField(null); setInfoPosition(null); if (onSplitField) onSplitField(selectedField); }}
                          title="Împarte câmpul în două"
                        >✂️ Taie</button>
                      )}

                      <button
                        style={{ padding: '5px 9px', background: '#ecf0f1', color: '#555', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.78rem' }}
                        onClick={(e) => { e.stopPropagation(); setSelectedField(null); setInfoPosition(null); if (onDeleteField) onDeleteField(selectedField.id); }}
                        title="Șterge câmpul"
                      >🗑️ Șterge</button>
                    </>
                  )}
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </LoadScript>
      
      {/* Improved Map Controls with labels for better accessibility */}
      <div className="map-controls-container">
        {/* Top-right corner controls */}
        <div className="map-controls top-right">
          <button
            className={`map-control-button labeled ${mapFullscreen ? 'active' : ''}`}
            onClick={toggleFullscreen}
            title={mapFullscreen ? "Exit fullscreen" : "Fullscreen view"}
          >
            {mapFullscreen ? <FaCompress /> : <FaExpand />}
            <span className="button-label">{mapFullscreen ? "Exit Fullscreen" : "Fullscreen"}</span>
          </button>
        </div>
        
        {/* Bottom-left corner controls */}
        <div className="map-controls bottom-left">
          <button
            className={`map-control-button labeled ${drawingMode ? 'active' : ''}`}
            onClick={() => handleDrawingMode(!drawingMode)}
            title={drawingMode ? "Cancel drawing" : "Draw new field"}
            disabled={!isLoaded}
          >
            <FaDrawPolygon />
            <span className="button-label">{drawingMode ? "Cancel Drawing" : "Draw Field"}</span>
          </button>
          
          {/* KML upload button */}
          <button
            className="map-control-button labeled"
            onClick={() => fileInputRef.current?.click()}
            title="Import field from file"
            disabled={!isLoaded}
          >
            <FaUpload />
            <span className="button-label">Import Field</span>
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".kml"
            style={{ display: 'none' }}
          />

          {/* Commune-boundaries toggle */}
          <button
            className={`map-control-button labeled ${showBoundaries ? 'active' : ''}`}
            onClick={() => setShowBoundaries(b => !b)}
            title={showBoundaries ? 'Ascunde limitele comunelor' : 'Arată limitele comunelor pe hartă'}
            disabled={!isLoaded || loadingBoundaries}
          >
            <span style={{ fontSize: '1rem', lineHeight: 1 }}>
              {loadingBoundaries ? '⏳' : '🗺️'}
            </span>
            <span className="button-label">
              {loadingBoundaries ? 'Se încarcă...' : showBoundaries ? 'Ascunde Limite' : 'Limite Comune'}
            </span>
          </button>
        </div>

        {/* Drawing-specific controls that appear only in drawing mode */}
        {drawingMode && (
          <div className="map-controls drawing-panel">
            <div className="drawing-status">
              {areaPreview ? (
                <div className="area-display">
                  <FaRuler /> <span>Field Area: {areaPreview} hectares</span>
                </div>
              ) : (
                <span>{getDrawingStatusText()}</span>
              )}
            </div>
            <div className="drawing-buttons">
              {polygonPath.length > 0 && (
                <button
                  className="map-control-button labeled"
                  onClick={handleRemoveLastPoint}
                  title="Remove last point"
                >
                  <FaUndo />
                  <span className="button-label">Undo Last</span>
                </button>
              )}
              {polygonPath.length > 0 && (
                <button
                  className="map-control-button labeled"
                  onClick={handleClearDrawing}
                  title="Clear all points and start over"
                >
                  <FaTrash />
                  <span className="button-label">Clear All</span>
                </button>
              )}
              {polygonPath.length >= 3 && (
                <button
                  className="map-control-button labeled finish"
                  onClick={handleFinishDrawing}
                  title={splitMode ? 'Aplică tăierea' : 'Salvează câmpul'}
                >
                  <FaCheck />
                  <span className="button-label">{splitMode ? 'Aplică Tăierea' : 'Salvează Câmpul'}</span>
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* In-map shape-editing controls — always visible, works in fullscreen too */}
        {editingFieldId && !drawingMode && (
          <div style={{
            position: 'absolute', top: 56, left: '50%', transform: 'translateX(-50%)',
            display: 'flex', gap: 8, alignItems: 'center',
            background: 'rgba(33,150,243,0.95)', backdropFilter: 'blur(4px)',
            color: 'white', padding: '8px 18px', borderRadius: 8,
            boxShadow: '0 2px 14px rgba(0,0,0,0.4)', zIndex: 10, whiteSpace: 'nowrap'
          }}>
            <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>✏️ Editare formă — trageți vârfurile</span>
            <button
              onClick={() => { if (onSaveShape) onSaveShape(); }}
              style={{ padding: '5px 14px', background: '#27ae60', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}
            >✅ Finalizează Editarea</button>
            <button
              onClick={() => { if (onCancelShape) onCancelShape(); }}
              style={{ padding: '5px 12px', background: 'rgba(255,255,255,0.22)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
            >✕ Anulează</button>
          </div>
        )}

        {/* Drawing / split instructions */}
        {drawingMode && (
          <div className="map-controls drawing-help">
            <div className="help-text">
              {splitMode ? (
                <>
                  <strong>✂️ Mod Tăiere:</strong>
                  <ol>
                    <li>Click oriunde pe sau în jurul câmpului roșu</li>
                    <li>Trasați conturul porțiunii care se separă</li>
                    <li>Minim 3 puncte, apoi click „Aplică Tăierea"</li>
                  </ol>
                </>
              ) : (
                <>
                  <strong>Cum desenați un câmp:</strong>
                  <ol>
                    <li>Click pe hartă pentru a plasa puncte</li>
                    <li>Minim 3 puncte pentru un poligon valid</li>
                    <li>Click „Salvează Câmpul" când ați terminat</li>
                  </ol>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Mobile Location Request Modal */}
      {showLocationRequest && (
        <div className="location-request-modal">
          <div className="location-request-content">
            <h3>📍 Enable Location for Better Drawing</h3>
            <p>
              To draw fields more accurately on mobile, we can use your current location 
              to center the map and provide better precision.
            </p>
            <div className="location-request-buttons">
              <button 
                className="btn btn-primary"
                onClick={requestLocation}
                disabled={locationPermission === 'requesting'}
              >
                {locationPermission === 'requesting' ? 'Getting Location...' : 'Allow Location'}
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowLocationRequest(false)}
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
});

// Utility function to show a field on map
export const showFieldOnMap = (map, field) => {
  if (map && field) {
    if (window.google && window.google.maps) {
      const bounds = new window.google.maps.LatLngBounds();
      const coordinates = field.coordinates || [];
      
      if (coordinates.length >= 3) {
        coordinates.forEach(point => {
          bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
        });
        
        // Use panToBounds instead of fitBounds for smoother movement
        map.panToBounds(bounds, 50); // 50px padding
        
        // Zoom out slightly to provide context, but more gently
        setTimeout(() => {
          const zoom = map.getZoom();
          if (zoom > 16) {
            map.setZoom(16);
          }
        }, 300);
      }
    }
  }
};

// React.memo prevents re-render when parent (Fields.js) has unrelated state changes
// (e.g. search filter, selected field in list) — only re-renders when map-relevant props change.
export default React.memo(MapComponent);